import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../context';
import type { LibraryQuery, PlexItem } from '../plex';
import { Card } from '../components/Card';
import { hubForLibrary, useLayout } from '../layout';
import { WidgetView } from '../components/Widgets';
import { linkProps } from '../router';

const PAGE = 60;

const SORTS = [
  { id: 'titleSort', label: 'Titel' },
  { id: 'addedAt:desc', label: 'Recent toegevoegd' },
  { id: 'originallyAvailableAt:desc', label: 'Nieuwste eerst' },
  { id: 'audienceRating:desc', label: 'Beoordeling' },
  { id: 'lastViewedAt:desc', label: 'Laatst bekeken' },
];

type Filters = Omit<LibraryQuery, 'start' | 'size'>;

/** Bibliotheek bladeren: raster met filters (focusbare knoppen, dus ook met afstandsbediening). */
export function Library({ sectionKey }: { sectionKey: string }) {
  const { plex, libraries } = useApp();
  const lib = libraries.find((l) => l.key === sectionKey);
  const layout = useLayout();
  const hub = hubForLibrary(layout, sectionKey, libraries);
  const hubWidgets = hub?.widgets.filter((w) => w.enabled) ?? [];

  const [filters, setFilters] = useState<Filters>({ sort: 'titleSort' });
  const [items, setItems] = useState<PlexItem[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [genres, setGenres] = useState<{ key: string; title: string }[]>([]);
  const [showGenres, setShowGenres] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    plex.genres(sectionKey).then(setGenres).catch(() => setGenres([]));
  }, [plex, sectionKey]);

  const load = useCallback(
    async (start: number) => {
      const id = ++requestId.current;
      setLoading(true);
      setError(null);
      try {
        const res = await plex.libraryItems(sectionKey, { ...filters, start, size: PAGE });
        if (id !== requestId.current) return; // verouderd antwoord na filterwissel
        setItems((prev) => (start === 0 ? res.items : [...prev, ...res.items]));
        setTotal(res.total);
      } catch {
        if (id === requestId.current) setError('Laden mislukt.');
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [plex, sectionKey, filters],
  );

  useEffect(() => {
    setItems([]);
    setTotal(null);
    void load(0);
  }, [load]);

  // Live bibliotheek: iets toegevoegd aan deze bibliotheek → eerste pagina stil verversen
  useEffect(() => {
    const onChange = (e: Event) => {
      const sections = (e as CustomEvent<{ sections: string[] }>).detail?.sections ?? [];
      if (sections.length && !sections.includes(String(sectionKey)) && !sections.includes('')) return;
      if (items.length <= PAGE) void load(0);
    };
    window.addEventListener('tumedia:library-changed', onChange);
    return () => window.removeEventListener('tumedia:library-changed', onChange);
  }, [load, sectionKey, items.length]);

  // Volgende pagina laden zodra het einde in beeld komt
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loading && total !== null && items.length < total) void load(items.length);
    }, { rootMargin: '800px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [load, loading, total, items.length]);

  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));
  const genreTitle = genres.find((g) => g.key === filters.genre)?.title;

  return (
    <main className={`page ${['spotlight', 'billboard'].includes(hubWidgets[0]?.view ?? '') ? 'lib-has-spotlight' : ''}`}>
      {hubWidgets.length > 0 && (
        <div className="lib-hub" aria-label={`Hub ${hub!.name}`}>
          {hubWidgets.map((w) => <WidgetView key={w.id} w={w} />)}
        </div>
      )}
      <header className="page-head">
        <h1>{hubWidgets.length ? `Alles in ${lib?.title ?? 'bibliotheek'}` : lib?.title ?? 'Bibliotheek'}</h1>
        {total !== null && <span className="subtle">{total} titels</span>}
        {hub && <a className="row-more" {...linkProps('/settings/home')}>Widgets aanpassen</a>}
      </header>

      <div className="filterbar" role="toolbar" aria-label="Sorteren en filteren" data-focus-group>
        {SORTS.map((s) => (
          <button key={s.id} className="chip" aria-pressed={filters.sort === s.id} onClick={() => set({ sort: s.id })}>{s.label}</button>
        ))}
        <span className="chip-sep" aria-hidden="true" />
        <button className="chip" aria-pressed={!!filters.unwatched} onClick={() => set({ unwatched: !filters.unwatched })}>Ongezien</button>
        <button className="chip" aria-pressed={filters.resolution === '4k'} onClick={() => set({ resolution: filters.resolution === '4k' ? undefined : '4k' })}>4K</button>
        <button className="chip" aria-pressed={!!filters.hdr} onClick={() => set({ hdr: !filters.hdr })}>HDR</button>
        {genres.length > 0 && (
          <button className="chip" aria-expanded={showGenres} aria-pressed={!!filters.genre} onClick={() => setShowGenres((v) => !v)}>
            {genreTitle ? `Genre: ${genreTitle}` : 'Genre'}
          </button>
        )}
      </div>

      {showGenres && (
        <div className="filterbar genres" role="group" aria-label="Genre" data-focus-group>
          <button className="chip" aria-pressed={!filters.genre} onClick={() => { set({ genre: undefined }); setShowGenres(false); }}>Alle</button>
          {genres.map((g) => (
            <button key={g.key} className="chip" aria-pressed={filters.genre === g.key} onClick={() => { set({ genre: g.key }); setShowGenres(false); }}>
              {g.title}
            </button>
          ))}
        </div>
      )}

      {error && <p className="placeholder-row error" role="alert">{error}</p>}
      {total === 0 && !loading && <p className="placeholder-row">Niets gevonden met deze filters.</p>}

      <div className="grid" data-focus-group>
        {items.map((item) => <Card key={item.ratingKey} item={item} kind="poster" className="card-grid" />)}
      </div>
      <div ref={sentinel} aria-hidden="true" style={{ height: 1 }} />
      {loading && <p className="subtle" style={{ textAlign: 'center' }} aria-live="polite">Laden…</p>}
      <div className="footer-space" />
    </main>
  );
}
